import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { invalidateTaskQueries, formatUserName } from "@/lib/task-query-utils";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarSrc } from "@/components/user-avatar";
import { Label } from "@/components/ui/label";
import { User } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getBoardLabel } from "@/lib/board-types";
import type { TaskUser, BoardMember } from "./task-types";

interface TaskAssigneeSelectProps {
  taskId: string;
  assignee: TaskUser | null;
  members: BoardMember[];
  canEdit: boolean;
  boardType?: string;
  hideLabel?: boolean;
}

export function TaskAssigneeSelect({ taskId, assignee, members, canEdit, boardType, hideLabel }: TaskAssigneeSelectProps) {
  const { toast } = useToast();
  const label = getBoardLabel(boardType, "assignee");

  const updateMutation = useMutation({
    mutationFn: async (userId: string | null) =>
      apiRequest("PATCH", `/api/tasks/${taskId}`, { assigneeUserId: userId }),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) =>
      toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  if (!canEdit) {
    return (
      <div className="flex items-center gap-1.5">
        {!hideLabel && <span className="text-xs text-muted-foreground">{label}:</span>}
        {assignee ? (
          <div className="flex items-center gap-1">
            <Avatar className="h-5 w-5">
              {assignee.avatarUrl && (
                <AvatarImage src={getAvatarSrc(assignee.avatarUrl, assignee.id)} alt={formatUserName(assignee)} />
              )}
              <AvatarFallback className="text-[9px]">
                {formatUserName(assignee).slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm" data-testid="task-text-assignee">
              {formatUserName(assignee)}
            </span>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground italic" data-testid="task-text-assignee">Unassigned</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {!hideLabel && <Label className="text-xs text-muted-foreground whitespace-nowrap">{label}:</Label>}
      <Select
        value={assignee?.id ?? "__unassigned__"}
        onValueChange={v => updateMutation.mutate(v === "__unassigned__" ? null : v)}
        disabled={updateMutation.isPending}
      >
        <SelectTrigger className="h-7 w-auto min-w-[120px] text-sm gap-1" data-testid="task-select-assignee-inline">
          {assignee ? (
            <div className="flex items-center gap-1">
              <Avatar className="h-4 w-4">
                {assignee.avatarUrl && (
                  <AvatarImage src={getAvatarSrc(assignee.avatarUrl, assignee.id)} alt={formatUserName(assignee)} />
                )}
                <AvatarFallback className="text-[8px]">
                  {formatUserName(assignee).slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="truncate">{formatUserName(assignee)}</span>
            </div>
          ) : (
            <span className="text-muted-foreground italic">Unassigned</span>
          )}
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__unassigned__">Unassigned</SelectItem>
          {members.map(m => (
            <SelectItem key={m.userId} value={m.userId} data-testid={`option-assignee-${m.userId}`}>
              {formatUserName(m)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
